import { AppDispatch } from "../../../app/store";
import { AgendaMomentInterface } from "../../../common/interfaces/AgendaMomentInterface";
import DossierInterface from "../../../common/interfaces/DossierInterface";
import { PatchStepsEnum } from "../../../common/constants/PatchStepsEnum";
import { GeneralStepsEnum } from "../../../common/constants/GeneralStepsEnum";
import { dossierTypeEnum } from "../../../common/constants/dossierTypeEnum";
import { fundingTypeEnum } from "../../../common/constants/fundingTypeEnum";
import { setDossierOptions, updateDossier } from "../../../app/rootReducer";
import { setGeneralStep } from "../../helpType/helpTypeSlice";
import {
    AppointmentInterface,
    setActiveAgendaMoment,
} from "../appointmentSlice";

interface GlassDamageLocationValues {
    rearWindowValue?: number;
    roofWindowValue?: number;
    sideWindowValue?: number;
}

const hasEstablishmentAvailability = (
    agendaMoment: AgendaMomentInterface,
    dossier: DossierInterface | undefined,
    fundingType: string | undefined,
): boolean => {
    if (
        fundingType !== fundingTypeEnum.Self &&
        agendaMoment.availability?.dates &&
        typeof dossier?.appointment_type !== "string" &&
        dossier?.appointment_type?.code !== dossierTypeEnum.CaravanRepair
    ) {
        return !!agendaMoment.availability.dates.find(
            (availabilityDate) => availabilityDate.has_capacity,
        );
    }

    return false;
};

export const isEstablishmentSelectable = (
    agendaMoment: AgendaMomentInterface,
    dossier: DossierInterface | undefined,
    fundingType: string | undefined,
    glassDamageLocation: number,
    glassDamageLocationValues: GlassDamageLocationValues,
): boolean => {
    const { rearWindowValue, roofWindowValue, sideWindowValue } =
        glassDamageLocationValues;

    return (
        hasEstablishmentAvailability(agendaMoment, dossier, fundingType) ||
        fundingType === fundingTypeEnum.Self ||
        (typeof dossier?.appointment_type !== "string" &&
            dossier?.appointment_type?.code ===
                dossierTypeEnum.CaravanRepair) ||
        glassDamageLocation === rearWindowValue ||
        glassDamageLocation === roofWindowValue ||
        glassDamageLocation === sideWindowValue
    );
};

export const filterEstablishmentsFromResponse = (
    responseData: AgendaMomentInterface[],
    fundingType: string | undefined,
    isCaravanDamage: boolean,
    glassDamageLocation: number,
    glassDamageLocationValues: GlassDamageLocationValues,
): AgendaMomentInterface[] => {
    const { rearWindowValue, roofWindowValue, sideWindowValue } =
        glassDamageLocationValues;

    if (
        fundingType !== fundingTypeEnum.Self &&
        !isCaravanDamage &&
        glassDamageLocation !== rearWindowValue &&
        glassDamageLocation !== roofWindowValue &&
        glassDamageLocation !== sideWindowValue
    ) {
        return responseData.filter((agendaMoment) =>
            agendaMoment.availability?.dates?.find(
                (availability) => availability.has_capacity,
            ),
        );
    }

    return responseData;
};

export const selectAgendaMoment = (
    dispatch: AppDispatch,
    agendaMoment: AgendaMomentInterface,
    dossier: DossierInterface | undefined,
    agendaMoments: AppointmentInterface["agendaMoments"],
): void => {
    const previousEstablishmentId =
        agendaMoments.activeAgendaMoment?.establishment?.id;

    if (agendaMoments.activeAgendaMoment) {
        if (
            agendaMoments.activeAgendaMoment.establishment.id ===
            agendaMoment.establishment.id
        ) {
            dispatch(setActiveAgendaMoment(agendaMoment));
        }
        return;
    }

    dispatch(
        setDossierOptions({
            allowsAppointments:
                agendaMoment?.establishment?.allows_appointments,
            allowsLoanVehicles:
                agendaMoment?.establishment?.allows_loan_vehicles,
            thankYouMessage:
                agendaMoment?.establishment?.establishment_type_data
                    .thank_you_message,
            sendImageCaptureRequest:
                agendaMoment?.establishment?.establishment_type_data
                    .send_image_capture_request,
        }),
    );

    if (
        typeof dossier?.appointment_type !== "string" &&
        dossier?.appointment_type?.code !== dossierTypeEnum.CaravanRepair &&
        agendaMoment?.establishment?.allows_appointments === true
    ) {
        dispatch(setActiveAgendaMoment(agendaMoment));
        dispatch(setGeneralStep(GeneralStepsEnum.Appointment));
        dispatch(
            updateDossier({
                data: {
                    establishment_id: agendaMoment.establishment.id,
                },
                method: PatchStepsEnum.PatchEstablishmentType,
            }),
        );
        return;
    }

    if (
        typeof dossier?.appointment_type !== "string" &&
        dossier?.appointment_type?.code !== dossierTypeEnum.CaravanRepair &&
        agendaMoment?.establishment?.allows_appointments === false
    ) {
        dispatch(setGeneralStep(GeneralStepsEnum.Appointment));
        dispatch(setActiveAgendaMoment(agendaMoment));
        dispatch(
            updateDossier({
                data: {
                    establishment_id: agendaMoment.establishment.id,
                },
                method: PatchStepsEnum.PatchEstablishmentId,
            }),
        );
        return;
    }

    dispatch(setGeneralStep(GeneralStepsEnum.Appointment));
    dispatch(setActiveAgendaMoment(agendaMoment));

    if (
        typeof dossier?.appointment_type !== "string" &&
        dossier?.appointment_type?.code === dossierTypeEnum.CaravanRepair &&
        previousEstablishmentId !== agendaMoment.establishment.id
    ) {
        dispatch(setGeneralStep(GeneralStepsEnum.Form));
    }
};
